import React, { useMemo, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../context";
import useFetchObjects from "../../../api/useFetchObjects";
import axios from "axios";
import { toast } from "react-toastify";
import SaveIcon from "@mui/icons-material/Save";

const toMonthStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};

const daysInMonth = (year, month) => new Date(year, month, 0).getDate();

const userDisplay = (u) => {
  if (!u) return "—";
  const name = [u.first_name, u.last_name].filter(Boolean).join(" ").trim();
  return name || u.username || "—";
};

export default function NightShiftPlanning() {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const today = new Date();
  const [month, setMonth] = useState(toMonthStr(today));
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [saving, setSaving] = useState(false);
  const [checked, setChecked] = useState({}); // { "userId-date": true }

  const [year, monthNum] = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    return [y, m];
  }, [month]);
  const days = useMemo(() => daysInMonth(year, monthNum), [year, monthNum]);
  const dateStrings = useMemo(() => {
    const arr = [];
    for (let d = 1; d <= days; d++) {
      arr.push(`${year}-${String(monthNum).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
    }
    return arr;
  }, [year, monthNum, days]);

  const { data: usersData } = useFetchObjects(
    ["users-night-shift", departmentFilter],
    `users/?page_size=500${departmentFilter ? `&department=${departmentFilter}` : ""}`,
    token
  );
  const users = useMemo(() => {
    const raw = usersData?.results ?? usersData ?? [];
    return Array.isArray(raw) ? raw : [];
  }, [usersData]);

  const { data: assignmentsData } = useFetchObjects(
    ["night-shift-assignments-planning", month],
    `night-shift-assignments/?month=${month}&page_size=500`,
    token
  );
  const assignments = useMemo(() => {
    const raw = assignmentsData?.results ?? assignmentsData ?? [];
    return Array.isArray(raw) ? raw : [];
  }, [assignmentsData]);

  const initialChecked = useMemo(() => {
    const obj = {};
    assignments.forEach((a) => {
      const key = `${a.user}-${a.date}`;
      obj[key] = true;
    });
    return obj;
  }, [assignments]);

  React.useEffect(() => {
    setChecked(initialChecked);
  }, [month, initialChecked]);

  const toggle = useCallback((userId, dateStr) => {
    setChecked((prev) => {
      const key = `${userId}-${dateStr}`;
      return { ...prev, [key]: !prev[key] };
    });
  }, []);

  const isChecked = useCallback(
    (userId, dateStr) => checked[`${userId}-${dateStr}`] === true,
    [checked]
  );

  const handleSave = async () => {
    const assignmentsToSend = [];
    users.forEach((u) => {
      dateStrings.forEach((dateStr) => {
        if (isChecked(u.id, dateStr)) {
          assignmentsToSend.push({ user: u.id, date: dateStr });
        }
      });
    });
    setSaving(true);
    try {
      const base = process.env.REACT_APP_API_URL || "";
      await axios.post(
        `${base}/night-shift-assignments/bulk-update/`,
        { month, assignments: assignmentsToSend },
        { headers: { Authorization: `Token ${token}` } }
      );
      toast.success(t("nightShift.saved", "Schedule saved."));
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || t("nightShift.saveError", "Failed to save."));
    } finally {
      setSaving(false);
    }
  };

  const { data: departmentsData } = useFetchObjects(
    ["departments-night-shift"],
    "departments/?page_size=200",
    token
  );
  const departments = useMemo(() => {
    const raw = departmentsData?.results ?? departmentsData ?? [];
    return Array.isArray(raw) ? raw : [];
  }, [departmentsData]);

  return (
    <Box sx={{ p: 2 }}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t("nightShift.planningTitle", "Assign nights")}
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2, alignItems: "center" }}>
          <TextField
            type="month"
            label={t("nightShift.month", "Month")}
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t("nightShift.filterDepartment", "Department")}</InputLabel>
            <Select
              value={departmentFilter}
              label={t("nightShift.filterDepartment", "Department")}
              onChange={(e) => setDepartmentFilter(e.target.value)}
            >
              <MenuItem value="">{t("common.all", "All")}</MenuItem>
              {departments.map((d) => (
                <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {t("common.save", "Save")}
          </Button>
        </Box>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small" stickyHeader sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.200" }}>
                <TableCell sx={{ minWidth: 180 }}><strong>{t("nightShift.employee", "Employee")}</strong></TableCell>
                <TableCell sx={{ minWidth: 120 }}><strong>{t("nightShift.department", "Department")}</strong></TableCell>
                {dateStrings.map((d) => {
                  const dateObj = new Date(d + "T12:00:00");
                  const dayNum = dateObj.getDate();
                  const dayOfWeek = dateObj.toLocaleDateString(undefined, { weekday: "short" });
                  return (
                    <TableCell key={d} align="center" sx={{ minWidth: 44, px: 0.5 }}>
                      <Box component="span" sx={{ display: "block", fontWeight: 600 }}>{dayNum}</Box>
                      <Box component="span" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>{dayOfWeek}</Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={dateStrings.length + 2} align="center" sx={{ py: 3 }}>
                    {t("nightShift.noUsers", "No users to display.")}
                  </TableCell>
                </TableRow>
              ) : (
                users.map((u) => (
                  <TableRow key={u.id} hover>
                    <TableCell>{userDisplay(u)}</TableCell>
                    <TableCell>{u.display_department || "—"}</TableCell>
                    {dateStrings.map((dateStr) => (
                      <TableCell key={dateStr} align="center" padding="checkbox" sx={{ px: 0.5 }}>
                        <Checkbox
                          size="small"
                          checked={isChecked(u.id, dateStr)}
                          onChange={() => toggle(u.id, dateStr)}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </Box>
  );
}
